/**
 * AI Suggestion Card Component
 *
 * Displays an individual AI suggestion with:
 * - Code name and valence indicator
 * - Confidence score with color coding
 * - Reasoning and text evidence (in learning mode)
 * - Accept/Reject buttons
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { VALENCE_COLORS, DIMENSION_COLORS } from '../../constants/workbenchConfig';

/**
 * Get confidence color based on score
 */
function getConfidenceColor(confidence) {
  if (confidence >= 0.85) return '#23A38F'; // High - teal
  if (confidence >= 0.7) return '#D8A200'; // Medium - gold
  return '#767676'; // Low - gray
}

/**
 * AI Suggestion Card
 */
// eslint-disable-next-line max-lines-per-function
function AISuggestionCard({
  suggestion,
  onAccept,
  onReject,
  learningMode,
  isProcessing,
}) {
  const [expanded, setExpanded] = useState(learningMode);

  const confidenceColor = getConfidenceColor(suggestion.confidence);
  const confidencePercent = Math.round(suggestion.confidence * 100);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '2px dashed',
        borderColor: 'rgba(0, 151, 178, 0.4)',
        borderRadius: 2,
        bgcolor: 'rgba(0, 151, 178, 0.04)',
        position: 'relative',
        opacity: isProcessing ? 0.7 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'rgba(0, 151, 178, 0.6)',
          bgcolor: 'rgba(0, 151, 178, 0.08)',
        },
      }}
    >
      {/* AI Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          left: 8,
          bgcolor: '#0097B2',
          color: '#fff',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: '0.65rem',
          fontWeight: 600,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 12 }} />
        AI Suggestion
      </Box>
      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 1,
          mb: 1,
        }}
      >
        {/* Code Name with Valence */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: VALENCE_COLORS[suggestion.valence],
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {suggestion.displayName || suggestion.code.replace(/_/g, ' ')}
          </Typography>
        </Box>

        {/* Accept/Reject Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Accept (A)">
            <IconButton
              size="small"
              onClick={onAccept}
              disabled={isProcessing}
              sx={{
                bgcolor: 'rgba(35, 163, 143, 0.1)',
                color: '#23A38F',
                '&:hover': {
                  bgcolor: 'rgba(35, 163, 143, 0.2)',
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject (X)">
            <IconButton
              size="small"
              onClick={onReject}
              disabled={isProcessing}
              sx={{
                bgcolor: 'rgba(255, 55, 73, 0.1)',
                color: '#FF3749',
                '&:hover': {
                  bgcolor: 'rgba(255, 55, 73, 0.2)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Confidence + Dimension */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          label={`${confidencePercent}%`}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: `${confidenceColor}20`,
            color: confidenceColor,
          }}
        />
        <Chip
          label={suggestion.primaryDimension}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            bgcolor: `${DIMENSION_COLORS[suggestion.primaryDimension]}15`,
            color: DIMENSION_COLORS[suggestion.primaryDimension],
          }}
        />
        {!learningMode && suggestion.reasoning && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 'auto', p: 0.5 }}
          >
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
      </Box>
      {/* Confidence Bar */}
      <LinearProgress
        variant="determinate"
        value={confidencePercent}
        sx={{
          height: 4,
          borderRadius: 2,
          mb: 1,
          bgcolor: 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': {
            bgcolor: confidenceColor,
          },
        }}
      />
      {/* Expandable Details */}
      <Collapse in={expanded || learningMode}>
        {/* Reasoning */}
        {suggestion.reasoning && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {suggestion.reasoning}
          </Typography>
        )}

        {/* Text Evidence */}
        {suggestion.textEvidence && suggestion.textEvidence.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Evidence:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {suggestion.textEvidence.map((evidence, i) => (
                <Chip
                  key={i}
                  label={`"${evidence}"`}
                  size="small"
                  sx={{
                    height: 'auto',
                    py: 0.5,
                    fontSize: '0.65rem',
                    fontStyle: 'italic',
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

/**
 * AI Suggestions List Component
 */
export function AISuggestionsList({
  suggestions,
  onAccept,
  onReject,
  onAcceptAll,
  learningMode,
  isLoading,
  processingId,
  hasFetched,
}) {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Analyzing chunk for behavioral patterns...
        </Typography>
        <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {hasFetched
          ? 'No behavioral patterns detected in this chunk.'
          : 'Select a chunk to get AI suggestions, or press Alt+G'}
      </Typography>
    );
  }

  return (
    <Box>
      {/* Accept All Button */}
      {suggestions.length > 1 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={onAcceptAll}
            sx={{ fontSize: '0.75rem' }}
          >
            Accept All ({suggestions.length})
          </Button>
        </Box>
      )}

      {/* Suggestions */}
      {suggestions.map((suggestion, index) => (
        <AISuggestionCard
          key={`${suggestion.code}-${index}`}
          suggestion={suggestion}
          onAccept={() => onAccept(suggestion)}
          onReject={() => onReject(suggestion)}
          learningMode={learningMode}
          isProcessing={processingId === suggestion.code}
        />
      ))}
    </Box>
  );
}

export default AISuggestionCard;
